import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from '../usePantryName';
import Feather from '@expo/vector-icons/Feather';
import { useSelector, useDispatch } from 'react-redux';
import { Paprika, PaprikaSpacing, PaprikaFreshness } from '../../../styles/theme';
import { InventoryItem } from '../../../redux/inventorySlice';
import { PantryRecipeSuggestion, recipesForPantryItem, getScoreBadgeColor } from '../../../services/pantryRecipeService';
import { selectRecipeIndex } from '../../../redux/recipeIndexSlice';
import {
  AddToMealPlanModal,
  type AddToMealPlanAction,
  type AddToMealPlanOptions,
} from '../../recipe-detail/AddToMealPlanModal';
import { MealType, addToWeek } from '../../../redux/mealPlanSlice';
import { RootState, AppDispatch } from '../../../redux/store';
import { trackEvent } from '../../../services/analyticsService';
import { setPendingPantryToast } from '../pendingToast';
import { RemoteImage } from '../../shared/RemoteImage';
import { parseIsoDate } from '../../../utils/localDate';

interface Props {
  item: InventoryItem | null;
  itemId: string;
  onClose: () => void;
}

export function RecipesPanel({ item, itemId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const pantryName = usePantryName();
  const dispatch = useDispatch<AppDispatch>();
  const inventory = useSelector((state: RootState) =>
    state.inventory?.items || []
  );
  const recipeIndex = useSelector(selectRecipeIndex);

  const [selectedRecipe, setSelectedRecipe] = useState<PantryRecipeSuggestion | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Calculate suggestions
  const suggestions = useCallback(() => {
    if (!item || !recipeIndex) return [];
    return recipesForPantryItem(item, recipeIndex, inventory, 5);
  }, [item, recipeIndex, inventory]);

  const recipes = suggestions();

  const handleAddToMeal = useCallback((
    date: string,
    mealType: MealType,
    _action?: AddToMealPlanAction,
    _fromMealId?: string,
    options?: AddToMealPlanOptions,
  ) => {
    if (!selectedRecipe) return;

    const entry = selectedRecipe.entry;

    // Build Recipe object from PersonalRecipeIndexEntry
    const recipePayload = {
      id: String(entry.id),
      title: entry.title,
      image: entry.image || '',
      readyInMinutes: entry.readyInMinutes || 0,
      servings: 4,
      healthScore: entry.healthScore || 0,
      dishTypes: entry.dishTypes || [],
      cuisines: entry.cuisines || [],
      diets: entry.diets || [],
      extendedIngredients: (entry.ingredients || []).map((name, idx) => ({
        id: idx,
        name,
        original: name,
        amount: 0,
        unit: '',
      })),
      instructions: [],
      summary: '',
    };

    dispatch(addToWeek({
      recipe: recipePayload,
      scheduledFor: date,
      servings: 4,
      mealType,
      cooked: options?.cooked,
    }));

    // Show toast with placed message
    const dayName = parseIsoDate(date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
    });

    const toastMsg = t('pantry.plan.placed', {
      recipe: entry.title,
      day: dayName,
      meal: t(`thisWeek.mealTypes.${mealType}`),
    });
    setPendingPantryToast(toastMsg);

    // Track analytics
    trackEvent('pantry_recipe_planned_from_pantry', {
      recipeId: entry.id,
      recipeTitle: entry.title,
      score: selectedRecipe.score,
    });

    // Close sheets
    setShowAddModal(false);
    setSelectedRecipe(null);
    onClose();
  }, [selectedRecipe, dispatch, t, i18n, onClose]);

  if (!item) return null;

  return (
    <>
      <View style={styles.root}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {t('pantry.cook.recipesTitle', { name: pantryName(item) })}
            </Text>
          </View>
          <Pressable onPress={onClose} hitSlop={8}>
            <Feather name="x" size={20} color={Paprika.textMuted} />
          </Pressable>
        </View>

        {/* Recipes list */}
        {recipes.length === 0 ? (
          <View style={styles.empty}>
            <Feather name="search" size={32} color={Paprika.textMuted} />
            <Text style={styles.emptyText}>
              {t('pantry.cook.nothingUrgent')}
            </Text>
          </View>
        ) : (
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          >
            {recipes.map((suggestion) => (
              <DesktopRecipeCard
                key={suggestion.entry.id}
                suggestion={suggestion}
                onPress={() => {
                  setSelectedRecipe(suggestion);
                  setShowAddModal(true);
                }}
              />
            ))}
          </ScrollView>
        )}
      </View>

      {/* Add to meal modal */}
      {selectedRecipe && (
        <AddToMealPlanModal
          visible={showAddModal}
          recipeTitle={selectedRecipe.entry.title}
          onClose={() => {
            setShowAddModal(false);
            setSelectedRecipe(null);
          }}
          onAdd={handleAddToMeal}
        />
      )}
    </>
  );
}

interface RecipeCardProps {
  suggestion: PantryRecipeSuggestion;
  onPress: () => void;
}

function DesktopRecipeCard({ suggestion, onPress }: RecipeCardProps) {
  const { t } = useTranslation();
  const recipe = suggestion.entry;

  // Score badge color (uses fresh tones for ≥80)
  const scoreColor = getScoreBadgeColor(suggestion.score);

  return (
    <Pressable onPress={onPress} style={styles.card}>
      {/* Recipe image - smaller for desktop */}
      <RemoteImage
        uri={recipe.image}
        variant="thumb"
        style={styles.image}
        fallback={null}
      />

      {/* Recipe info */}
      <View style={styles.cardContent}>
        <View style={styles.titleRow}>
          <Text style={styles.recipeTitle} numberOfLines={2}>
            {recipe.title}
          </Text>
          <View style={[styles.scoreBadge, { backgroundColor: scoreColor }]}>
            <Text style={styles.scoreText}>
              {suggestion.matched.length}/{recipe.ingredients.length}
            </Text>
          </View>
        </View>

        {/* Cook time and ingredients count */}
        <Text style={styles.meta}>
          {recipe.readyInMinutes} min · {t('pantry.cook.haveIngredients', {
            have: suggestion.matched.length,
            total: recipe.ingredients.length,
          })}
        </Text>

        {/* Ingredient chips - compact for desktop */}
        <View style={styles.ingredients}>
          {suggestion.matched.slice(0, 2).map((ing, idx) => (
            <View key={`matched-${idx}`} style={styles.ingredientChip}>
              <Feather name="check" size={10} color={PaprikaFreshness.fresh.text} />
              <Text style={[styles.ingredientText, { color: PaprikaFreshness.fresh.text }]} numberOfLines={1}>
                {ing}
              </Text>
            </View>
          ))}
          {suggestion.missing.slice(0, 1).map((ing, idx) => (
            <View key={`missing-${idx}`} style={styles.ingredientChipMissing}>
              <Text style={styles.ingredientTextMissing} numberOfLines={1}>
                {ing}
              </Text>
            </View>
          ))}
          {(suggestion.matched.length > 2 || suggestion.missing.length > 1) && (
            <Text style={styles.ingredientTextMissing} numberOfLines={1}>
              +more
            </Text>
          )}
        </View>

        {/* CTA */}
        <Pressable style={styles.ctaButton} onPress={onPress}>
          <Text style={styles.ctaText}>
            {t('pantry.cook.addToMeal')}
          </Text>
        </Pressable>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
    borderLeftWidth: 1,
    borderLeftColor: Paprika.hairline,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textPrimary,
  },
  list: {
    flex: 1,
  },
  listContent: {
    padding: PaprikaSpacing.sm,
    gap: PaprikaSpacing.sm,
    paddingBottom: PaprikaSpacing.md,
  },
  empty: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 8,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 100,
    backgroundColor: Paprika.surfaceLow,
  },
  cardContent: {
    padding: PaprikaSpacing.sm,
    gap: 6,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
  },
  recipeTitle: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textPrimary,
  },
  scoreBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 32,
  },
  scoreText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    color: '#ffffff',
  },
  meta: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  ingredients: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 2,
  },
  ingredientChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: PaprikaFreshness.fresh.bg,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  ingredientChipMissing: {
    backgroundColor: Paprika.surfaceLow,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  ingredientText: {
    fontFamily: 'Inter-Regular',
    fontSize: 9,
  },
  ingredientTextMissing: {
    fontFamily: 'Inter-Regular',
    fontSize: 9,
    color: Paprika.textMuted,
  },
  ctaButton: {
    backgroundColor: Paprika.accent,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  ctaText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: '#ffffff',
  },
});
